import type { Conversation, CrmContact, Inbox, NotificationMute } from '../../api/types';
import { buildCallOptions, RC_DIAL_FAILED, type CallOption } from './callOptions';
import { useComms } from './context';
import { useDialer } from './dialer';
import { useNotifications } from './notifications';
import { usePhone } from './phone';

/**
 * What the thread menus offer — the half of ThreadControls.tsx that is not a
 * component. Both layouts read these, for that file's reason: a duration or a
 * call route spelled twice is one the two layouts will come to disagree on.
 */

/** A host's action runner: closes its menu, reports a refusal, re-reads the row. */
export type Run = (action: () => Promise<unknown>) => void;

export const snoozeUntil = (hours: number) => new Date(Date.now() + hours * 3600_000).toISOString();

export const SNOOZE_OPTIONS: { label: string; hours: number }[] = [
  { label: 'For 1 hour', hours: 1 },
  { label: 'For 4 hours', hours: 4 },
  { label: 'Until tomorrow', hours: 24 },
  { label: 'Until next week', hours: 24 * 7 },
];

/** This conversation's mute, if it has one — never its watch row. */
export function useConversationMute(conversationId: number): NotificationMute | null {
  const override = useNotifications().muteFor('conversation', conversationId);
  return override?.mode === 'watch' ? null : override;
}

/** The Assign button's title: who holds the thread now, since nothing else on screen says. */
export function assignTitle(
  conversation: Conversation,
  agents: { dashBoardId: number; name: string }[],
  meId: number | null | undefined,
) {
  if (conversation.assigneeIsBot) return 'Assigned to the assistant — reassign';
  const holder = conversation.assigneeDashBoardId;
  if (holder === null) return 'Assign';
  // Compared as strings, like ThreadPane's assignee test: the two ids reach the
  // console by different routes and have disagreed about their type before.
  if (meId != null && String(holder) === String(meId)) return 'Assigned to you — reassign';
  const name = agents.find((agent) => String(agent.dashBoardId) === String(holder))?.name;
  return name ? `Assigned to ${name} — reassign` : 'Assign';
}

/**
 * Every route a call to this contact could take, best first.
 *
 * The order is the rule this screen has always followed — the thread's own
 * channel leads, because calling back from the number the customer has been
 * messaging is the one caller id they will recognise — but the alternatives
 * are listed instead of hidden. Hiding them is what left an agent stuck when
 * RingOut had no number to ring and a registered browser phone sat one inbox
 * away.
 */
export function useThreadCallOptions({
  contact,
  inbox,
  onRun,
  onError,
}: {
  contact: CrmContact;
  inbox: Inbox | undefined;
  onRun: Run;
  /** Say why a call did not start, or clear what was said. */
  onError: (message: string | null) => void;
}): CallOption[] {
  const { source, inboxes, canCall, capabilities } = useComms();
  const phone = usePhone();
  const rcDialer = useDialer();
  return buildCallOptions({
    to: contact.phoneNumber,
    threadInboxId: inbox?.id ?? null,
    inboxes,
    canCall,
    phone,
    rcDialer,
    agentPhone: capabilities?.phone ?? null,
    // `undefined` is a server that predates me.phone, not an agent without a
    // number: an old backend under a new console would grey out every RingOut.
    agentPhoneKnown: capabilities !== null && capabilities.phone !== undefined,
    dial: (to) => void phone?.call(to),
    ringOut: (inboxId) => onRun(() => source.api.ringoutCall(contact.id, { inboxId })),
    // The dial resolves to whether the widget placed the call; a false is the
    // one thing an agent pressing Call most needs to hear.
    rcDial: (to) => {
      onError(null);
      void rcDialer?.dial(to).then((started) => {
        if (!started) onError(RC_DIAL_FAILED);
      });
    },
  });
}
